/**
 * Global Preferences Context Provider
 * Auto-initializes and manages preferences for authenticated users
 * Provides global access to preferences system via context
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { usePreferences } from './use-preferences';

export interface GlobalPreferencesContextType {
  preferences: ReturnType<typeof usePreferences>;
  isReady: boolean;
}

const GlobalPreferencesContext = createContext<GlobalPreferencesContextType | null>(null);

/**
 * Provider component - wrap authenticated app with this
 * Auto-initializes preferences when userId becomes available
 */
export function PreferencesProvider({
  children,
  userId,
  wsUrl,
}: {
  children: React.ReactNode;
  userId: string;
  wsUrl: string;
}) {
  const preferences = usePreferences({
    userId,
    wsUrl,
    syncInterval: 5000,
    conflictStrategy: 'server-wins',
  });

  const [isReady, setIsReady] = useState(false);

  // Mark ready when preferences initialize
  useEffect(() => {
    if (preferences.isInitialized && !preferences.isLoading) {
      setIsReady(true);
    }
  }, [preferences.isInitialized, preferences.isLoading]);

  const value = {
    preferences,
    isReady,
  };

  return (
    <GlobalPreferencesContext.Provider value={value}>{children}</GlobalPreferencesContext.Provider>
  );
}

/**
 * Hook to access global preferences from anywhere in the app
 * Must be inside PreferencesProvider
 */
export function useGlobalPreferences(): GlobalPreferencesContextType {
  const ctx = useContext(GlobalPreferencesContext);
  if (!ctx) {
    throw new Error('useGlobalPreferences must be called inside PreferencesProvider');
  }
  return ctx;
}
